import { createAsyncThunk, createSlice, PayloadAction, ActionReducerMapBuilder } from '@reduxjs/toolkit';
import axios from 'axios';
import API, { typeThrownException } from '@acorn-portal/shared/src/services/AxiosInstance';
import {
	TelematicsDriverScoreClient,
	DriverAverageScoresDto,
	DriverScoreInsightsDto,
} from '@acorn-portal/shared/src/services/telematicsDriverScoreApi';

interface TelematicsDriverScoreState {
	// Average scores
	averageScores: DriverAverageScoresDto | null;
	averageScoresLoading: boolean;
	averageScoresError: string | null;
	// Insights
	insights: DriverScoreInsightsDto | null;
	insightsLoading: boolean;
	insightsError: string | null;
}

const initialState: TelematicsDriverScoreState = {
	averageScores: null,
	averageScoresLoading: false,
	averageScoresError: null,
	insights: null,
	insightsLoading: false,
	insightsError: null,
};

interface FetchAverageScoresParams {
	policyReference: string;
}

interface FetchInsightsParams {
	policyReference: string;
	periodDays?: number; // Valid values: 7, 14, or 30. Defaults to 7.
}

export const fetchAverageScores = createAsyncThunk<
	DriverAverageScoresDto,
	FetchAverageScoresParams,
	{ rejectValue: unknown }
>('telematicsDriverScore/fetchAverageScores', async (params: FetchAverageScoresParams, { rejectWithValue }) => {
	try {
		const client = new TelematicsDriverScoreClient(undefined, API);
		const response = await client.average(params.policyReference);
		return response;
	} catch (e) {
		if (axios.isAxiosError(e) && e.response) {
			if (e.response.status < 200 || e.response.status > 300) {
				return rejectWithValue(e);
			}
		}
		if (typeThrownException(e) && e.status) {
			if (e.status < 200 || e.status > 300) {
				return rejectWithValue(e);
			}
		}
		throw e;
	}
});

export const fetchInsights = createAsyncThunk<DriverScoreInsightsDto, FetchInsightsParams, { rejectValue: unknown }>(
	'telematicsDriverScore/fetchInsights',
	async (params: FetchInsightsParams, { rejectWithValue }) => {
		try {
			const client = new TelematicsDriverScoreClient(undefined, API);
			const response = await client.insights(params.policyReference, params.periodDays);
			return response;
		} catch (e) {
			if (axios.isAxiosError(e) && e.response) {
				if (e.response.status < 200 || e.response.status > 300) {
					return rejectWithValue(e);
				}
			}
			if (typeThrownException(e) && e.status) {
				if (e.status < 200 || e.status > 300) {
					return rejectWithValue(e);
				}
			}
			throw e;
		}
	},
);

export const telematicsDriverScoreSlice = createSlice({
	name: 'telematicsDriverScore',
	initialState,
	reducers: {
		setAverageScores: (state: TelematicsDriverScoreState, action: PayloadAction<DriverAverageScoresDto>) => {
			state.averageScores = action.payload;
		},
		setInsights: (state: TelematicsDriverScoreState, action: PayloadAction<DriverScoreInsightsDto>) => {
			state.insights = action.payload;
		},
		clearAverageScores: (state: TelematicsDriverScoreState) => {
			state.averageScores = null;
			state.averageScoresLoading = false;
			state.averageScoresError = null;
		},
		clearInsights: (state: TelematicsDriverScoreState) => {
			state.insights = null;
			state.insightsLoading = false;
			state.insightsError = null;
		},
		clearAll: () => initialState,
	},
	extraReducers: (builder: ActionReducerMapBuilder<TelematicsDriverScoreState>) => {
		// Average Scores
		builder.addCase(fetchAverageScores.pending, (state: TelematicsDriverScoreState) => {
			state.averageScoresLoading = true;
			state.averageScoresError = null;
		});
		builder.addCase(fetchAverageScores.fulfilled, (state: TelematicsDriverScoreState, { payload }) => {
			state.averageScoresLoading = false;
			state.averageScores = payload;
		});
		builder.addCase(fetchAverageScores.rejected, (state: TelematicsDriverScoreState, action) => {
			state.averageScoresLoading = false;
			state.averageScoresError = action.error.message ?? 'Failed to fetch average scores';
		});

		// Insights
		builder.addCase(fetchInsights.pending, (state: TelematicsDriverScoreState) => {
			state.insightsLoading = true;
			state.insightsError = null;
		});
		builder.addCase(fetchInsights.fulfilled, (state: TelematicsDriverScoreState, { payload }) => {
			state.insightsLoading = false;
			state.insights = payload;
		});
		builder.addCase(fetchInsights.rejected, (state: TelematicsDriverScoreState, action) => {
			state.insightsLoading = false;
			state.insightsError = action.error.message ?? 'Failed to fetch score insights';
		});
	},
});

export const { setAverageScores, setInsights, clearAverageScores, clearInsights, clearAll } =
	telematicsDriverScoreSlice.actions;
export default telematicsDriverScoreSlice.reducer;
